import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { FaPlus, FaSave, FaTrash, FaSync, FaArrowUp, FaArrowDown, FaSearch, FaStar, FaArrowLeft } from 'react-icons/fa';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import api from '../../../api/apiClient';

const initialTestimonialState = {
  id: null,
  author_name: '',
  author_title: '',
  company: '',
  avatar_key: '',
  rating: '',
  quote_html: '',
  is_active: true,
  is_highlighted: false
};

const TestimonialQuoteEditor = ({ value, onChange, disabled }) => {
  const isUpdatingFromProps = useRef(false);
  
  const editor = useEditor(
    {
      extensions: [StarterKit],
      content: value || '',
      editable: !disabled,
      onUpdate({ editor }) {
        if (isUpdatingFromProps.current) return;
        const html = editor.getHTML();
        onChange(html);
      }
    },
    []
  );

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      isUpdatingFromProps.current = true;
      editor.commands.setContent(value || '', false);
      setTimeout(() => {
        isUpdatingFromProps.current = false;
      }, 0);
    }
  }, [editor, value]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  if (!editor) {
    return (
      <div className="border rounded p-2 bg-gray-50 text-gray-500 text-sm">Loading editor...</div>
    );
  }

  return (
    <div className="border rounded" style={{ minHeight: '160px' }}>
      <EditorContent editor={editor} className="p-2" />
    </div>
  );
};

const TestimonialManager = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [testimonials, setTestimonials] = useState([]);
  const [testimonialForm, setTestimonialForm] = useState(initialTestimonialState);
  const [selectedTestimonialId, setSelectedTestimonialId] = useState(null);

  const [savingTestimonial, setSavingTestimonial] = useState(false);

  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [filterHighlighted, setFilterHighlighted] = useState('all');

  const normalize = useCallback((value) => {
    if (!value || typeof value !== 'string') return '';
    return value.trim();
  }, []);

  const loadTestimonials = useCallback(async () => {
    const params = {};
    if (search && search.trim()) params.search = search.trim();
    if (filterActive === 'active') params.is_active = '1';
    if (filterActive === 'inactive') params.is_active = '0';
    if (filterHighlighted === 'highlighted') params.is_highlighted = '1';
    if (filterHighlighted === 'non-highlighted') params.is_highlighted = '0';

    const response = await api.testimonials.list(params);
    if (response.data?.success) {
      setTestimonials(response.data.data || []);
    }
  }, [search, filterActive, filterHighlighted]);

  const fetchAll = useCallback(async ({ showLoader = true } = {}) => {
    try {
      if (showLoader) setLoading(true);
      await loadTestimonials();
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [loadTestimonials]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleRefresh = useCallback(async () => {
    if (loading || refreshing) return;
    setRefreshing(true);
    try {
      await fetchAll({ showLoader: false });
    } finally {
      setRefreshing(false);
    }
  }, [fetchAll, loading, refreshing]);

  const sortedTestimonials = useMemo(() => {
    return [...testimonials].sort((a, b) => a.position - b.position || new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
  }, [testimonials]);

  const handleTestimonialFormChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;
    setTestimonialForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleQuoteChange = useCallback((html) => {
    setTestimonialForm((prev) => ({
      ...prev,
      quote_html: html || ''
    }));
  }, []);

  const handleNewTestimonial = useCallback(() => {
    setSelectedTestimonialId(null);
    setTestimonialForm(initialTestimonialState);
  }, []);

  const handleEditTestimonial = useCallback((testimonial) => {
    if (!testimonial) return;
    setSelectedTestimonialId(testimonial.id);
    setTestimonialForm({
      id: testimonial.id,
      author_name: testimonial.author_name || '',
      author_title: testimonial.author_title || '',
      company: testimonial.company || '',
      avatar_key: testimonial.avatar_key || '',
      rating: typeof testimonial.rating === 'number' ? String(testimonial.rating) : testimonial.rating || '',
      quote_html: testimonial.quote_html || '',
      is_active: !!testimonial.is_active,
      is_highlighted: !!testimonial.is_highlighted
    });
  }, []);

  const handleSaveTestimonial = useCallback(async (event) => {
    event.preventDefault();

    const author_name = normalize(testimonialForm.author_name);
    const quote_html = testimonialForm.quote_html || '';
    if (!author_name || !quote_html.trim()) return;

    setSavingTestimonial(true);
    try {
      const payload = {
        author_name,
        author_title: normalize(testimonialForm.author_title) || null,
        company: normalize(testimonialForm.company) || null,
        avatar_key: normalize(testimonialForm.avatar_key) || null,
        rating: testimonialForm.rating ? parseInt(testimonialForm.rating, 10) || null : null,
        quote_html,
        is_active: !!testimonialForm.is_active,
        is_highlighted: !!testimonialForm.is_highlighted
      };

      if (testimonialForm.id) {
        await api.testimonials.update(testimonialForm.id, payload);
      } else {
        await api.testimonials.create(payload);
      }

      await loadTestimonials();
      handleNewTestimonial();
    } finally {
      setSavingTestimonial(false);
    }
  }, [testimonialForm, handleNewTestimonial, loadTestimonials, normalize]);

  const handleDeleteTestimonial = useCallback(async (testimonialId) => {
    if (!testimonialId) return;
    await api.testimonials.remove(testimonialId);
    if (selectedTestimonialId === testimonialId) {
      handleNewTestimonial();
    }
    await loadTestimonials();
  }, [handleNewTestimonial, loadTestimonials, selectedTestimonialId]);

  const changeTestimonialOrder = useCallback(async (testimonialId, direction) => {
    const items = sortedTestimonials.map((t) => ({ id: t.id, position: t.position }));
    const index = items.findIndex((x) => x.id === testimonialId);
    if (index === -1) return;

    if (direction === 'up' && index > 0) {
      const tmp = items[index - 1].position;
      items[index - 1].position = items[index].position;
      items[index].position = tmp;
    }
    if (direction === 'down' && index < items.length - 1) {
      const tmp = items[index + 1].position;
      items[index + 1].position = items[index].position;
      items[index].position = tmp;
    }

    await api.testimonials.reorder(items);
    await loadTestimonials();
  }, [loadTestimonials, sortedTestimonials]);

  useEffect(() => {
    loadTestimonials();
  }, [loadTestimonials]);

  const renderRating = (rating) => {
    if (!rating || rating <= 0) return <span className="text-muted small">-</span>;
    const stars = Math.max(1, Math.min(5, rating));
    return (
      <span className="text-warning small">
        {Array.from({ length: stars }).map((_, idx) => (
          <FaStar key={idx} style={{ marginRight: 2 }} />
        ))}
      </span>
    );
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-3">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/site-management')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
          >
            <FaArrowLeft className="w-4 h-4" /> Back
          </button>
          <div>
            <h3 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Testimonials Management</h3>
            <p className="text-sm text-gray-600 mt-1">Manage customer quotes and success stories with author details, optional rating, and rich text quotes.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleRefresh}
            disabled={loading || refreshing}
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-8 py-2"
          >
            {refreshing ? 'Refreshing...' : (<><FaSync className="inline-block mr-2"/> Refresh</>)}
          </Button>
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleNewTestimonial}
            disabled={savingTestimonial}
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-8 py-2"
          >
            <FaPlus className="inline-block mr-2" /> New Testimonial
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-5">
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Search</label>
            <div className="flex items-center bg-white border border-purple-200 rounded-lg px-3 py-2">
              <FaSearch className="text-gray-400 mr-2" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search author, title, company or quote..." className="w-full outline-none text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Status</label>
            <select value={filterActive} onChange={(e) => setFilterActive(e.target.value)} className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select">
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Highlight</label>
            <select value={filterHighlighted} onChange={(e) => setFilterHighlighted(e.target.value)} className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select">
              <option value="all">All</option>
              <option value="highlighted">Highlighted only</option>
              <option value="non-highlighted">Non-highlighted only</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <h6 className="text-lg font-bold mb-3">Testimonials</h6>
          {loading ? (
            <div className="text-center py-6"><div className="animate-spin h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div></div>
          ) : (
            <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
              <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="text-left py-3 px-4">Author</th>
                    <th className="text-left py-3 px-4">Title / Company</th>
                    <th className="text-center py-3 px-4" style={{ width: '120px' }}>Rating</th>
                    <th className="text-center py-3 px-4" style={{ width: '110px' }}>Highlight</th>
                    <th className="text-center py-3 px-4" style={{ width: '100px' }}>Status</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-purple-50">
                  {sortedTestimonials.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-gray-500 py-6">No testimonials found. Use the editor below to create your first testimonial.</td>
                    </tr>
                  ) : (
                    sortedTestimonials.map((testimonial, index) => (
                      <tr key={testimonial.id}>
                        <td className="py-3 px-4 align-top">
                          <button onClick={() => handleEditTestimonial(testimonial)} className="text-left text-sm font-medium text-purple-800 hover:underline">{testimonial.author_name}</button>
                        </td>
                        <td className="py-3 px-4 align-top text-sm text-gray-600">
                          {testimonial.author_title && <div>{testimonial.author_title}</div>}
                          {testimonial.company && <div className="text-gray-500 text-xs">{testimonial.company}</div>}
                        </td>
                        <td className="py-3 px-3 text-left">
                          {renderRating(testimonial.rating)}
                        </td>
                        <td className="py-3 px-3 text-left">
                          {testimonial.is_highlighted ? (
                            <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold">Highlighted</span>
                          ) : (
                            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">Normal</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-left">
                          {testimonial.is_active ? (
                            <span className="inline-block px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-semibold">Active</span>
                          ) : (
                            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">Inactive</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="d-flex gap-1 justify-content-end">
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => changeTestimonialOrder(testimonial.id, 'up')}
                              disabled={index === 0}
                              title="Move Up"
                              className="border-gray-300 text-gray-600 hover:bg-gray-100 rounded p-1"
                            >
                              <FaArrowUp className="w-5 h-5" />
                            </Button>
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => changeTestimonialOrder(testimonial.id, 'down')}
                              disabled={index === sortedTestimonials.length - 1}
                              title="Move Down"
                              className="border-gray-300 text-gray-600 hover:bg-gray-100 rounded p-1"
                            >
                              <FaArrowDown className="w-5 h-5" />
                            </Button>
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => handleDeleteTestimonial(testimonial.id)}
                              title="Delete Testimonial"
                              className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1"
                            >
                              <FaTrash className="w-5 h-5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t">
          <h6 className="text-lg font-bold mb-3">{selectedTestimonialId ? 'Edit Testimonial' : 'New Testimonial'}</h6>
          <form onSubmit={handleSaveTestimonial} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Author name</label>
              <input type="text" name="author_name" value={testimonialForm.author_name} onChange={handleTestimonialFormChange} disabled={savingTestimonial} placeholder="Name of the person giving the testimonial" className="w-full px-4 py-2 border border-purple-200 rounded-lg" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Author title (optional)</label>
                <input type="text" name="author_title" value={testimonialForm.author_title} onChange={handleTestimonialFormChange} disabled={savingTestimonial} placeholder="e.g. HR Director" className="w-full px-4 py-2 border border-purple-200 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Company (optional)</label>
                <input type="text" name="company" value={testimonialForm.company} onChange={handleTestimonialFormChange} disabled={savingTestimonial} placeholder="e.g. ACME Corp" className="w-full px-4 py-2 border border-purple-200 rounded-lg" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Avatar key (optional)</label>
                <input type="text" name="avatar_key" value={testimonialForm.avatar_key} onChange={handleTestimonialFormChange} disabled={savingTestimonial} placeholder="Key used by frontend to resolve avatar image" className="w-full px-4 py-2 border border-purple-200 rounded-lg" />
                <p className="text-xs text-gray-500 mt-1">Store only a key or filename reference here (no upload).</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Rating (1-5, optional)</label>
                <input type="number" min={1} max={5} name="rating" value={testimonialForm.rating} onChange={handleTestimonialFormChange} disabled={savingTestimonial} placeholder="e.g. 5" className="w-full px-4 py-2 border border-purple-200 rounded-lg" />
              </div>
              <div className="flex flex-col justify-start">
                <label className="text-sm font-medium text-gray-700 mb-1">Flags</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" name="is_active" checked={testimonialForm.is_active} onChange={handleTestimonialFormChange} disabled={savingTestimonial} className="w-4 h-4" />
                    <span>Active</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" name="is_highlighted" checked={testimonialForm.is_highlighted} onChange={handleTestimonialFormChange} disabled={savingTestimonial} className="w-4 h-4" />
                    <span>Highlighted</span>
                  </label>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Quote</label>
              <TestimonialQuoteEditor value={testimonialForm.quote_html} onChange={handleQuoteChange} disabled={savingTestimonial} />
              <p className="text-xs text-gray-500 mt-2">Use this rich text editor to enter the testimonial quote. HTML is stored and rendered on the public view.</p>
            </div>

            <div className="flex justify-end items-center gap-3">
              {selectedTestimonialId && (
                <Button
                  bsPrefix="custom-hamburger"
                  type="button"
                  onClick={handleNewTestimonial}
                  disabled={savingTestimonial}
                  className="border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm px-4 py-2"
                >
                  Cancel
                </Button>
              )}
              <Button
                bsPrefix="custom-hamburger"
                type="submit"
                disabled={savingTestimonial}
                className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm px-4 py-2 flex items-center gap-2"
              >
                {savingTestimonial ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="w-4 h-4" />
                    Save Testimonial
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TestimonialManager;
